import {
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { FaImage, FaVideo } from "react-icons/fa";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { IoCloseSharp } from "react-icons/io5";
import { ReactSVG } from "react-svg";
import MediaPreviewModal from "../../components/MediaPreviewModal";
import { noImgShapes } from "../../lib/noImgShapes";
import { formatPrice, formatCarat } from "../../lib/formatters";
import Loader from "../../components/Loader";
import { CheckBox } from "./Checkbox";
import { useSettings } from "../../providers/SettingsProvider";
import DiamondListSkeleton from "./DiamondListSkeleton";

function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" && window.innerWidth <= breakpoint
    );
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, [breakpoint]);
    return isMobile;
}

function SortIcon({ active, order }) {
    if (!active) return null;
    return order === "asc" ? (
        <IoIosArrowUp className="mx-1 text-gray-500" />
    ) : (
        <IoIosArrowDown className="mx-1 text-gray-500" />
    );
}

function DiamondList({
    naturalData,
    loading,
    setRowSelection,
    rowSelection,
    from,
    onScrollEnd,
    moreLoading,
    handleViewDiamond,
    viewDiamondLoader,
    setTab,
}) {
    const { settings } = useSettings();
    const stroke = settings?.ifm_NaturalPrimaryColor;
    const { values, setValues, submitForm } = useFormikContext();
    const isMobile = useIsMobile();
    const [selectedRow, setSelectedRow] = useState(null);
    const [fromMedia, setFromMedia] = useState("image");
    const [isMediaLoading, setIsMediaLoading] = useState(true);

    const handleSort = useCallback(
        (field) => {
            // Don't write `page` into formik state - it's an infinite-query
            // concern, not a filter value, and would pollute sessionStorage.
            const next = {
                ...values,
                sort_field: field,
                sort_order:
                    values.sort_field === field && values.sort_order === "asc"
                        ? "desc"
                        : "asc",
            };
            setValues(next);
            submitForm();
        },
        [values, setValues, submitForm]
    );

    const handleSideDetail = useCallback(
        (data, kind) => {
            // Clicking same item + same kind = no-op (already showing).
            // Without this guard, setIsMediaLoading(true) re-mounts loader
            // overlay but onLoad doesn't refire on cached image → stuck.
            if (
                selectedRow?.STOCK_ID === data?.STOCK_ID &&
                fromMedia === kind
            ) {
                return;
            }
            setSelectedRow(data);
            setFromMedia(kind);
            setIsMediaLoading(true);
        },
        [selectedRow, fromMedia]
    );

    // Throttle scroll-end so it fires at most once per 400ms.
    const lastScrollEndRef = useRef(0);
    const handleScroll = useCallback(
        (e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            if (scrollHeight - scrollTop > clientHeight + 50) return;
            const now = Date.now();
            if (now - lastScrollEndRef.current < 400) return;
            lastScrollEndRef.current = now;
            onScrollEnd?.();
        },
        [onScrollEnd]
    );

    // Reset side-panel selection ONLY when `from` switches (e.g. result → compare).
    useEffect(() => {
        setSelectedRow(null);
    }, [from]);

    // Video iframe doesn't always fire onLoad reliably across browsers.
    // Auto-clear loader after 4s as fallback.
    useEffect(() => {
        if (fromMedia !== "image") {
            const t = setTimeout(() => setIsMediaLoading(false), 4000);
            return () => clearTimeout(t);
        }
    }, [selectedRow, fromMedia]);

    // Image loader fix: if user clicks image icon on the same row, src doesn't
    // change → browser doesn't refire onLoad → loader stuck. Check img.complete
    // after each selectedRow/fromMedia change and clear loader if already cached.
    const previewImgRef = useRef(null);
    useEffect(() => {
        if (!selectedRow || fromMedia !== "image") return;
        const img = previewImgRef.current;
        if (img && img.complete && img.naturalWidth > 0) {
            setIsMediaLoading(false);
        }
    }, [selectedRow, fromMedia]);

    const currencyName = naturalData?.[0]?.CURRENCY ?? null;

    const columns = useMemo(() => {
        const cols = [
            {
                id: "COMPARE",
                header: <div className="w-5">Compare</div>,
                cell: ({ row }) => (
                    <CheckBox
                        checked={row.getIsSelected()}
                        onChange={() => row.toggleSelected()}
                    />
                ),
            },
            {
                accessorKey: "SHAPE",
                header: () => "Shape",
                cell: (info) => (
                    <div className="flex items-center gap-2">
                        <ReactSVG
                            className="shape-icon"
                            src={info.row.original?.svg}
                            style={{ fill: stroke, stroke }}
                        />
                        <span>
                            {String(info.getValue() ?? "")
                                .charAt(0)
                                .toUpperCase() +
                                String(info.getValue() ?? "")
                                    .slice(1)
                                    .toLowerCase()}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "MEDIA",
                header: <div className="w-5">Media</div>,
                cell: (info) => (
                    <div className="flex items-center cursor-pointer">
                        <div
                            className={info.row.original.IMAGE ? "" : "opacity-0"}
                            onClick={() =>
                                info.row.original.IMAGE &&
                                handleSideDetail(info.row.original, "image")
                            }
                        >
                            <FaImage style={{ color: stroke }} />
                        </div>
                        <div
                            className={`ml-2 cursor-pointer ${
                                info.row.original.VIDEO ? "" : "opacity-0"
                            }`}
                            onClick={() =>
                                info.row.original.VIDEO &&
                                handleSideDetail(info.row.original, "video")
                            }
                        >
                            <FaVideo style={{ color: stroke }} />
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: "TOTAL_PRICE",
                header: () => (
                    <div
                        className="flex items-center cursor-pointer"
                        onClick={() => handleSort("price")}
                    >
                        Price{currencyName ? ` (${currencyName})` : ""}
                        <SortIcon
                            active={values.sort_field === "price"}
                            order={values.sort_order}
                        />
                    </div>
                ),
                cell: (info) => formatPrice(info.getValue(), info.row.original.CURRENCY),
            },
            {
                accessorKey: "CARAT",
                header: () => (
                    <div
                        className="flex items-center cursor-pointer"
                        onClick={() => handleSort("carat")}
                    >
                        Carat
                        <SortIcon
                            active={values.sort_field === "carat"}
                            order={values.sort_order}
                        />
                    </div>
                ),
                cell: (info) => formatCarat(info.getValue()),
            },
            {
                accessorKey: "COLOR",
                header: () => (
                    <div
                        className="flex items-center cursor-pointer"
                        onClick={() => handleSort("color")}
                    >
                        Color
                        <SortIcon
                            active={values.sort_field === "color"}
                            order={values.sort_order}
                        />
                    </div>
                ),
                cell: (info) => info.getValue(),
            },
            {
                accessorKey: "CLARITY",
                header: () => (
                    <div
                        className="flex items-center cursor-pointer"
                        onClick={() => handleSort("clarity")}
                    >
                        Clarity
                        <SortIcon
                            active={values.sort_field === "clarity"}
                            order={values.sort_order}
                        />
                    </div>
                ),
                cell: (info) => info.getValue(),
            },
            {
                accessorKey: "CUT",
                header: () => "Cut",
                cell: (info) => info.getValue() || "-",
            },
        ];

        if (selectedRow === null) {
            cols.push({
                accessorKey: "ACTION",
                header: () => "Action",
                size: 100,
                cell: (info) => (
                    <div className="inline-block">
                        <button
                            disabled={viewDiamondLoader}
                            className="btn-outline text-sm"
                            type="button"
                            onClick={() => handleViewDiamond(info.row.original)}
                        >
                            View Diamond
                        </button>
                    </div>
                ),
            });
        }
        return cols;
    }, [
        values.sort_field,
        values.sort_order,
        selectedRow,
        currencyName,
        stroke,
        handleSort,
        handleSideDetail,
        handleViewDiamond,
        viewDiamondLoader,
    ]);

    const table = useReactTable({
        data: naturalData || [],
        columns,
        manualFiltering: true,
        pageCount: 1,
        // Key rowSelection by STOCK_ID so selections survive reindexing
        // and compare tab can drive selection state by stable id.
        getRowId: (row) => String(row?.STOCK_ID ?? ""),
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: true,
        state: { rowSelection },
        onRowSelectionChange: setRowSelection,
    });

    return (
        <div>
            <div className="flex border">
                <div
                    className={`overflow-auto doc-table-container m-0 p-0 ${
                        !isMobile && selectedRow ? "w-7/12" : "w-full"
                    }`}
                    style={{ maxHeight: 600 }}
                    onScroll={handleScroll}
                >
                    <table>
                        <thead className="border-b">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            className="px-2 whitespace-nowrap py-1 text-left"
                                            style={{ minWidth: 80 }}
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef.header,
                                                      header.getContext()
                                                  )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="100%">
                                        <DiamondListSkeleton rows={8} />
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className={
                                            row.original?.STOCK_ID === selectedRow?.STOCK_ID
                                                ? "selectedRowForMedia"
                                                : ""
                                        }
                                        style={{
                                            backgroundColor: row.getIsSelected()
                                                ? `${stroke}30`
                                                : undefined,
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td
                                                key={cell.id}
                                                className="px-2 whitespace-nowrap"
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    {from === "result" && moreLoading && (
                        <Loader message="Loading more Diamonds..." />
                    )}
                </div>
                {!isMobile && selectedRow && (
                    <div className="w-5/12 relative" style={{ maxHeight: 500 }}>
                        <div
                            className="close-doc-preview"
                            onClick={() => setSelectedRow(null)}
                        >
                            <IoCloseSharp />
                        </div>
                        <div className="flex flex-col items-center justify-between h-full overflow-hidden pt-2 mt-4">
                            <div className="relative text-center">
                                {isMediaLoading && (
                                    <div className="iframe-loader-overlay">
                                        <Loader message="Loading Media..." />
                                    </div>
                                )}
                                {fromMedia === "image" ? (
                                    <img
                                        ref={previewImgRef}
                                        className="w-7/12 mx-auto"
                                        onLoad={() =>
                                            setTimeout(() => setIsMediaLoading(false), 200)
                                        }
                                        onError={(e) => {
                                            setIsMediaLoading(false);
                                            e.currentTarget.src =
                                                selectedRow.sample || noImgShapes.noimage;
                                        }}
                                        alt={selectedRow.SHAPE}
                                        src={selectedRow.IMAGE || selectedRow.sample}
                                    />
                                ) : (
                                    <div
                                        className="pt-3 relative"
                                        style={{
                                            height: 360,
                                            width: 360,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <iframe
                                            title="Video"
                                            src={selectedRow.VIDEO}
                                            height="100%"
                                            width="100%"
                                            frameBorder="0"
                                            onLoad={() =>
                                                setTimeout(
                                                    () => setIsMediaLoading(false),
                                                    200
                                                )
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="text-sm text-center px-5 py-2">
                                <p>Diamond Stock Id: {selectedRow.STOCK_ID}</p>
                                <button
                                    disabled={viewDiamondLoader}
                                    type="button"
                                    className="btn-outline text-xl mt-2"
                                    onClick={() => handleViewDiamond(selectedRow)}
                                >
                                    view diamond
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {isMobile && selectedRow && (
                    <MediaPreviewModal
                        show={!!selectedRow}
                        onClose={() => setSelectedRow(null)}
                        mediaType={fromMedia}
                        item={selectedRow}
                        isMediaLoading={isMediaLoading}
                        setIsMediaLoading={setIsMediaLoading}
                    />
                )}
            </div>
        </div>
    );
}

export default memo(DiamondList);
